import { useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DialogSimple from '@/components/dialog/DialogSimple';
import { useSelector } from '@/stores';
import RTKNotes from './main-section/notes-partials/RTKNotes';
//
type ConfirmDialogProps = {
  isDialogOpen: boolean;
  handleCloseDialog: () => void;
  handleConfirmDialog: () => void;
  confirmRecurringChanges?: boolean;
  isSubmitting?: boolean;
  affectedSessions?: {
    date: string;
    dateIsPast: boolean;
    isLoading: boolean;
    sessionId: string;
    sessionOptions: { id: number; label: string }[];
    todayDate: string;
    onChangeDate: (value: string) => void;
    onChangeSession: (value: string) => void;
  };
};

export default function ConfirmRecurringChangesDialog({
  isDialogOpen,
  handleCloseDialog,
  handleConfirmDialog,
  confirmRecurringChanges,
  isSubmitting = false,
  affectedSessions,
}: ConfirmDialogProps) {
  const [radioButtonValue, setRadioButtonValue] = useState('');
  const [dropdownSelectValue, setDropdownSelectValue] = useState('');
  const { next_cleaning_sessions } = useSelector((state) => state.booking);
  const disableSaveButton =
    (confirmRecurringChanges ? radioButtonValue === '' : false) ||
    Boolean(
      affectedSessions &&
        (!affectedSessions.date || affectedSessions.dateIsPast)
    );

  return (
    <DialogSimple
      dialogTitle={`Confirm ${
        confirmRecurringChanges ? 'recurring event' : ''
      } changes`}
      open={isDialogOpen}
      loadingButtonText="Save changes"
      isSubmitting={isSubmitting}
      disableLoadingButton={disableSaveButton}
      disagreeButtonText="Cancel"
      disagreeButtonColor="inherit"
      onClickButtonLoading={() => handleConfirmDialog()}
      onClickButtonClose={() => {
        if (!isSubmitting) handleCloseDialog();
      }}
    >
      <Stack direction="column" spacing={2} sx={{ px: 5, py: 1 }}>
        <Stack spacing={2}>
          <Typography>
            Please reason it in a few words why these changes were necessary
          </Typography>
          <RTKNotes noteType="reason_for_change" />
        </Stack>
        {affectedSessions && (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Affected sessions</Typography>
            <TextField
              select
              label="First affected session"
              size="small"
              fullWidth
              value={affectedSessions.sessionId}
              onChange={(event) =>
                affectedSessions.onChangeSession(event.target.value)
              }
              disabled={affectedSessions.isLoading}
              helperText={
                affectedSessions.sessionOptions.length === 0
                  ? 'No upcoming sessions were returned'
                  : undefined
              }
            >
              <MenuItem value="">Custom date</MenuItem>
              {affectedSessions.sessionOptions.map((session) => (
                <MenuItem key={session.id} value={String(session.id)}>
                  {session.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Effective from date"
              type="date"
              size="small"
              fullWidth
              value={affectedSessions.date}
              onChange={(event) =>
                affectedSessions.onChangeDate(event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: affectedSessions.todayDate }}
              error={affectedSessions.dateIsPast}
              helperText={
                affectedSessions.dateIsPast
                  ? 'Choose today or a future date'
                  : undefined
              }
            />
          </Stack>
        )}
        {confirmRecurringChanges && (
          <>
            <FormControl>
              <FormLabel
                id="request-type-radio-buttons-group-label"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  fontWeight: 600,
                  pb: 0.5,
                }}
              >
                Request Type
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="request-type-radio-buttons-group-label"
                name="row-radio-buttons-group"
              >
                <FormControlLabel
                  value="one"
                  control={
                    <Radio
                      onChange={(e) => setRadioButtonValue(e.target.value)}
                    />
                  }
                  label="One cleaning only"
                />
                <FormControlLabel
                  value="all"
                  control={
                    <Radio
                      onChange={(e) => setRadioButtonValue(e.target.value)}
                    />
                  }
                  label="All future cleaning"
                />
              </RadioGroup>
            </FormControl>

            {radioButtonValue === 'one' && (
              <Stack direction="column" spacing={2}>
                <TextField
                  select
                  label="Select booking"
                  size="small"
                  value={dropdownSelectValue}
                  onChange={(e) => setDropdownSelectValue(e.target.value)}
                >
                  {next_cleaning_sessions.map((date, index) => (
                    <MenuItem key={index} value={date}>
                      {date}
                    </MenuItem>
                  ))}
                </TextField>
                {!!dropdownSelectValue.length && (
                  <Typography>
                    The changes will apply to the following date only:{' '}
                    <span style={{ fontWeight: 600 }}>
                      {dropdownSelectValue}
                    </span>
                  </Typography>
                )}
              </Stack>
            )}
            {radioButtonValue === 'all' && (
              <Typography>
                The changes will apply to{' '}
                <span style={{ fontWeight: 600 }}>all future bookings</span>
              </Typography>
            )}
          </>
        )}
      </Stack>
    </DialogSimple>
  );
}
